/**
 * NutriPoints Loyalty API Client
 * Handles all communication with the NutriPetPlan backend via Shopify App Proxy
 *
 * SHOPIFY APP PROXY FLOW:
 * =======================
 * 1. Frontend calls: /apps/loyalty/balance
 * 2. Shopify automatically proxies to: your-backend.com/shopify/balance
 * 3. Shopify adds query parameters:
 *    - logged_in_customer_id (if customer is logged in)
 *    - shop (your store domain)
 *    - timestamp
 *    - signature (HMAC for verification)
 * 4. Backend verifies HMAC signature
 * 5. Backend extracts customer ID from logged_in_customer_id
 * 6. Backend returns customer's loyalty data
 *
 * DEVELOPMENT MODE:
 * =================
 * When backend runs with NODE_ENV !== "production":
 * - HMAC verification is skipped
 * - Mock customer ID is provided for testing
 * - Allows development without App Proxy configuration
 *
 * @see https://shopify.dev/docs/apps/build/online-store/app-proxies
 */

class LoyaltyAPI {
  constructor() {
    // Use App Proxy path - Shopify automatically proxies /apps/loyalty/* to backend /shopify/*
    this.baseUrl = '/apps/loyalty';
    this.requestCache = new Map();
    this.cacheDuration = 30000; // 30 seconds

    // Mock mode for development - enable by setting window.LOYALTY_DEV_MODE = true
    this.mockMode = false;
    this.mockBalance = 450; // Default mock balance
  }

  /**
   * Enable mock mode for development testing
   * @param {number} balance - Mock balance to use (default: 450)
   */
  enableMockMode(balance = 450) {
    this.mockMode = true;
    this.mockBalance = balance;
  }

  /**
   * Disable mock mode
   */
  disableMockMode() {
    this.mockMode = false;
  }

  /**
   * Generate mock balance data for development
   * @returns {object} - Mock balance data
   */
  getMockBalanceData() {
    return {
      success: true,
      customer_id: 1,
      shopify_customer_id: 'mock-customer-123',
      email: 'dev@nutrivetplan.com',
      balance: this.mockBalance,
      available_tiers: this.getMockTiers().tiers.filter(t => t.points_cost <= this.mockBalance),
      recent_transactions: this.getMockTransactions(),
      tier_products: this.getMockTierProducts()
    };
  }

  /**
   * Generate mock tiers data
   * @returns {object} - Mock tiers data
   */
  getMockTiers() {
    return {
      success: true,
      tiers: [
        {
          key: 'tier_150',
          points_cost: 65,
          max_discount_eur: 1.5,
          label_pt: 'Tier 65 - até €1.50',
          label_es: 'Nivel 65 - hasta €1.50',
          label_en: 'Tier 65 - up to €1.50',
          description_pt: 'Escolha produtos até €1.50 gratuitamente',
          description_es: 'Elige productos hasta €1.50 gratis',
          description_en: 'Choose products up to €1.50 for free',
          available: this.mockBalance >= 65,
          points_remaining: Math.max(0, 65 - this.mockBalance)
        },
        {
          key: 'tier_300',
          points_cost: 140,
          max_discount_eur: 3.0,
          label_pt: 'Tier 140 - até €3.00',
          label_es: 'Nivel 140 - hasta €3.00',
          label_en: 'Tier 140 - up to €3.00',
          description_pt: 'Escolha produtos até €3.00 gratuitamente',
          description_es: 'Elige productos hasta €3.00 gratis',
          description_en: 'Choose products up to €3.00 for free',
          available: this.mockBalance >= 140,
          points_remaining: Math.max(0, 140 - this.mockBalance)
        },
        {
          key: 'tier_500',
          points_cost: 250,
          max_discount_eur: 5.0,
          label_pt: 'Tier 250 - até €5.00',
          label_es: 'Nivel 250 - hasta €5.00',
          label_en: 'Tier 250 - up to €5.00',
          description_pt: 'Escolha produtos até €5.00 gratuitamente',
          description_es: 'Elige productos hasta €5.00 gratis',
          description_en: 'Choose products up to €5.00 for free',
          available: this.mockBalance >= 250,
          points_remaining: Math.max(0, 250 - this.mockBalance)
        },
        {
          key: 'tier_700',
          points_cost: 400,
          max_discount_eur: 15.0,
          label_pt: 'Tier 400 - Aconselhamento Veterinário',
          label_es: 'Nivel 400 - Asesoramiento Veterinario',
          label_en: 'Tier 400 - Veterinary Advice',
          description_pt: 'Aconselhamento veterinário gratuito',
          description_es: 'Asesoramiento veterinario gratuito',
          description_en: 'Free veterinary advice',
          available: this.mockBalance >= 400,
          points_remaining: Math.max(0, 400 - this.mockBalance)
        }
      ],
      customer_balance: this.mockBalance
    };
  }

  /**
   * Generate mock transactions
   * @returns {array} - Mock transaction history
   */
  getMockTransactions() {
    const now = new Date();
    return [
      {
        transaction_id: 'mock-tx-1',
        type: 'earn',
        points: 45,
        source: 'order',
        description: 'Order #1234 — €45.99',
        reference_id: '1234',
        adjusted_by: null,
        created_at: new Date(now - 2 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        transaction_id: 'mock-tx-2',
        type: 'redeem',
        points: -65,
        source: 'redemption',
        description: 'Redeemed Tier 65 - Promo: NP-65-ABC123',
        reference_id: 'NP-65-ABC123',
        adjusted_by: null,
        created_at: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: null
      },
      {
        transaction_id: 'mock-tx-3',
        type: 'earn',
        points: 89,
        source: 'order',
        description: 'Order #1235 — €89.50',
        reference_id: '1235',
        adjusted_by: null,
        created_at: new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(now.getTime() + 358 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        transaction_id: 'mock-tx-4',
        type: 'earn',
        points: 120,
        source: 'order',
        description: 'Order #1236 — €120.00',
        reference_id: '1236',
        adjusted_by: null,
        created_at: new Date(now - 14 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(now.getTime() + 351 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        transaction_id: 'mock-tx-5',
        type: 'adjust',
        points: 50,
        source: 'admin',
        description: 'Admin adjustment - Welcome bonus',
        reference_id: null,
        adjusted_by: 'Admin User',
        created_at: new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString(),
        expires_at: new Date(now.getTime() + 335 * 24 * 60 * 60 * 1000).toISOString()
      }
    ];
  }

  /**
   * Generate mock tier products
   * @returns {object} - Mock products by tier
   */
  getMockTierProducts() {
    // Using emojis as centered icons for mock products
    return {
      tier_150: [
        {
          id: '12345678901',
          title: 'Premium Dog Treats',
          url: '/products/premium-dog-treats',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🦴%3C/text%3E%3C/svg%3E',
          description: 'Delicious treats your dog will love',
          variants: [
            { id: '98765432101', price: 150, availableForSale: true }
          ]
        },
        {
          id: '12345678902',
          title: 'Cat Toy Mouse',
          url: '/products/cat-toy-mouse',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🐭%3C/text%3E%3C/svg%3E',
          description: 'Keep your cat entertained for hours',
          variants: [
            { id: '98765432102', price: 149, availableForSale: true }
          ]
        },
        {
          id: '12345678903',
          title: 'Pet Shampoo',
          url: '/products/pet-shampoo',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🧴%3C/text%3E%3C/svg%3E',
          description: 'Gentle formula for sensitive skin',
          variants: [
            { id: '98765432103', price: 145, availableForSale: true }
          ]
        },
        {
          id: '12345678904',
          title: 'Pet Collar',
          url: '/products/pet-collar',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🎀%3C/text%3E%3C/svg%3E',
          description: 'Comfortable collar for daily wear',
          variants: [
            { id: '98765432104', price: 149, availableForSale: true }
          ]
        }
      ],
      tier_300: [
        {
          id: '12345678905',
          title: 'Premium Cat Food 2kg',
          url: '/products/premium-cat-food',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🐱%3C/text%3E%3C/svg%3E',
          description: 'High-quality nutrition for cats',
          variants: [
            { id: '98765432105', price: 299, availableForSale: true }
          ]
        },
        {
          id: '12345678906',
          title: 'Dog Grooming Kit',
          url: '/products/dog-grooming-kit',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E✂️%3C/text%3E%3C/svg%3E',
          description: 'Complete grooming set for home use',
          variants: [
            { id: '98765432106', price: 295, availableForSale: true }
          ]
        },
        {
          id: '12345678907',
          title: 'Pet Water Fountain',
          url: '/products/pet-water-fountain',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E💧%3C/text%3E%3C/svg%3E',
          description: 'Fresh flowing water for your pet',
          variants: [
            { id: '98765432107', price: 289, availableForSale: true }
          ]
        },
        {
          id: '12345678908',
          title: 'Pet Carrier Bag',
          url: '/products/pet-carrier',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🎒%3C/text%3E%3C/svg%3E',
          description: 'Comfortable carrier for travel',
          variants: [
            { id: '98765432108', price: 298, availableForSale: true }
          ]
        }
      ],
      tier_500: [
        {
          id: '12345678909',
          title: 'Luxury Pet Bed Large',
          url: '/products/luxury-pet-bed',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🛏️%3C/text%3E%3C/svg%3E',
          description: 'Ultra-comfortable bed for your pet',
          variants: [
            { id: '98765432109', price: 499, availableForSale: true }
          ]
        },
        {
          id: '12345678910',
          title: 'Premium Dog Food 5kg',
          url: '/products/premium-dog-food',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🐕%3C/text%3E%3C/svg%3E',
          description: 'Complete nutrition for active dogs',
          variants: [
            { id: '98765432110', price: 495, availableForSale: true }
          ]
        },
        {
          id: '12345678911',
          title: 'Automatic Pet Feeder',
          url: '/products/automatic-feeder',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E🍽️%3C/text%3E%3C/svg%3E',
          description: 'Schedule feeding times automatically',
          variants: [
            { id: '98765432111', price: 489, availableForSale: true }
          ]
        },
        {
          id: '12345678912',
          title: 'Pet Activity Tracker',
          url: '/products/activity-tracker',
          featured_image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ffffff" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="120"%3E⌚%3C/text%3E%3C/svg%3E',
          description: 'Monitor your pet\'s health and activity',
          variants: [
            { id: '98765432112', price: 499, availableForSale: true }
          ]
        }
      ],
      tier_700: []
    };
  }

  /**
   * Make an API request via Shopify App Proxy
   * Shopify automatically adds authentication parameters
   *
   * @param {string} endpoint - API endpoint path (e.g., '/balance', '/redeem')
   * @param {object} options - Fetch options
   * @returns {Promise<object>} - Response data
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultHeaders = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true', // Skip ngrok warning page
    };

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options.headers || {}),
      },
      credentials: 'same-origin', // Important for Shopify session cookies
    };

    try {
      const response = await fetch(url, config);

      // Get raw response text to see what we're actually receiving
      const responseText = await response.text();

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = JSON.parse(responseText);
        } catch (e) {
          console.error('[Loyalty API] Failed to parse error response as JSON:', e);
          console.error('[Loyalty API] Error response was HTML or invalid JSON');
        }
        throw new Error(errorData.error || `API request failed: ${response.status}`);
      }

      return JSON.parse(responseText);
    } catch (error) {
      console.error('Loyalty API Error:', error);
      throw error;
    }
  }

  /**
   * Get cached data if available and not expired
   * @param {string} key - Cache key
   * @returns {object|null} - Cached data or null
   */
  getCached(key) {
    const cached = this.requestCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.data;
    }
    return null;
  }

  /**
   * Set cache data
   * @param {string} key - Cache key
   * @param {object} data - Data to cache
   */
  setCache(key, data) {
    this.requestCache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.requestCache.clear();
  }

  /**
   * Get customer's loyalty balance and transaction history
   * Note: Customer ID is automatically added by Shopify App Proxy
   *
   * @returns {Promise<object>} - Balance data including balance, tiers, and transactions
   */
  async getBalance() {
    // Return mock data if mock mode is enabled
    if (this.mockMode) {
      return new Promise(resolve => {
        setTimeout(() => resolve(this.getMockBalanceData()), 300); // Simulate network delay
      });
    }

    const cacheKey = 'balance';
    const cached = this.getCached(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Shopify App Proxy automatically adds logged_in_customer_id to the request
      const data = await this.request('/balance');
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('[Loyalty API] Failed to fetch loyalty balance:', error);
      throw error;
    }
  }

  /**
   * Get all available reward tiers
   * @param {number} balance - Optional customer balance to check availability
   * @returns {Promise<object>} - Tiers data
   */
  async getTiers(balance = null) {
    // Return mock data if mock mode is enabled
    if (this.mockMode) {
      return new Promise(resolve => {
        setTimeout(() => resolve(this.getMockTiers()), 200); // Simulate network delay
      });
    }

    const cacheKey = 'tiers';
    const cached = this.getCached(cacheKey);
    if (cached && balance === null) {
      return cached;
    }

    try {
      const endpoint = balance !== null ? `/tiers?balance=${balance}` : '/tiers';
      const data = await this.request(endpoint);

      if (balance === null) {
        this.setCache(cacheKey, data);
      }

      return data;
    } catch (error) {
      console.error('[Loyalty API] Failed to fetch loyalty tiers:', error);
      throw error;
    }
  }

  /**
   * Redeem points for a reward tier
   * Note: Customer ID is automatically added by Shopify App Proxy
   *
   * @param {string} tierKey - Tier identifier (e.g., 'tier_150')
   * @param {string} productId - Optional Shopify product ID
   * @param {string} variantId - Optional Shopify variant ID
   * @returns {Promise<object>} - Redemption result with promo code (or email confirmation for vet tier)
   */
  async redeemPoints(tierKey, productId = null, variantId = null) {
    if (!tierKey) {
      throw new Error('Tier key is required');
    }

    // Return mock redemption if mock mode is enabled
    if (this.mockMode) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const tier = this.getMockTiers().tiers.find(t => t.key === tierKey);
          if (!tier) {
            reject(new Error('Invalid tier'));
            return;
          }

          if (this.mockBalance < tier.points_cost) {
            reject(new Error('Insufficient points'));
            return;
          }

          // Update mock balance
          this.mockBalance -= tier.points_cost;

          const promoCode = `NP-${tier.points_cost}-${Math.random().toString(16).substring(2, 10).toUpperCase()}`;
          resolve({
            success: true,
            promo_code: promoCode,
            points_deducted: tier.points_cost,
            remaining_balance: this.mockBalance,
            tier: tier
          });
        }, 500); // Simulate network delay
      });
    }

    try {
      const body = { tier: tierKey };

      // Add product/variant IDs if provided
      if (productId) body.productId = productId;
      if (variantId) body.variantId = variantId;

      // Shopify App Proxy automatically adds logged_in_customer_id
      const data = await this.request('/redeem', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      // Clear balance cache after successful redemption
      this.clearCache();

      return data;
    } catch (error) {
      console.error('[Loyalty API] Failed to redeem points:', error);
      throw error;
    }
  }

  /**
   * Get tier products (public endpoint, no login required)
   * @returns {Promise<object>} - Tier products organized by tier
   */
  async getTierProducts() {
    // Return mock data if mock mode is enabled
    if (this.mockMode) {
      return new Promise(resolve => {
        setTimeout(() => resolve({
          success: true,
          tier_products: this.getMockTierProducts()
        }), 200);
      });
    }

    const cacheKey = 'tier_products';
    const cached = this.getCached(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const data = await this.request('/tier-products');
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('[Loyalty API] Failed to fetch tier products:', error);
      // Return mock products as fallback
      return {
        success: true,
        tier_products: this.getMockTierProducts()
      };
    }
  }

  /**
   * Calculate points that will be earned from cart total
   * @param {number} cartTotalCents - Cart total in cents
   * @returns {number} - Points to be earned
   */
  calculatePointsFromCart(cartTotalCents) {
    // 1€ = 100 cents = 1 point
    return Math.floor(cartTotalCents / 100);
  }

  /**
   * Format points display
   * @param {number} points - Number of points
   * @returns {string} - Formatted points string
   */
  formatPoints(points) {
    return new Intl.NumberFormat('en-US').format(points);
  }

  /**
   * Format currency display
   * @param {number} amount - Amount in euros
   * @returns {string} - Formatted currency string
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }
}

// Initialize global API instance
if (typeof window !== 'undefined') {
  window.loyaltyAPI = new LoyaltyAPI();
}
